<style scoped>

  #financial-segment {
    min-height: 200px;
  }

</style>

<template>

  <div>

    <div class="ui large breadcrumb">
      <div class="bold section">{{ title }}</div>
    </div>

    <bookmark-button :label="title" />

    <div class="ui divider"></div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div>

    <div v-show="!loading" class="animated fadeIn">

      <div class="ui segment">
        <h4 class="ui dividing header">Empresa</h4>
        <div class="ui form">
          <div class="fields">              
            <div class="seven wide field">
              <label class="form-label">Razão Social <span class="red text">*</span></label>
              <input v-model="company.name">
            </div>
            <div class="five wide field">
              <label class="form-label">Nome Fantasia <span class="red text">*</span></label>
              <input v-model="company.shortName">
            </div>
            <div class="four wide field">
              <label class="form-label">CNPJ <span class="red text">*</span></label>
              <the-mask type="tel" mask="##.###.###/####-##" v-model="company.cnpj" />
            </div> 
          </div>
          <div class="fields">                           
            <div class="five wide field">
              <label class="form-label">E-mail</label>
              <input type="email" v-model="company.email" @blur="company.email = $clearEmail($event)">
            </div>
            <div class="three wide field">
              <label class="form-label">Telefone</label>
              <input type="tel" v-model="company.phone">
            </div>
            <div class="eight wide field">
              <label class="form-label">Endereço</label>
              <input v-model="company.address">
            </div>
          </div>
          <div class="fields">
            <div class="five wide field">
              <label class="form-label">Município</label>
              <input v-model="company.city">
            </div>
            <div class="five wide field">
              <label class="form-label">Estado</label>
              <select-state ref="selectState" @onChange="onChangeState" />
            </div>
            <div class="six wide field">
              <label class="form-label">Site</label>
              <input v-model="company.site">
            </div>
          </div>
          <div class="fields">
            <div class="five wide field">
              <label class="form-label">Instagram</label>
              <input v-model="company.instagram">
            </div>
            <div class="five wide field">
              <label class="form-label">Facebook</label>
              <input v-model="company.facebook">
            </div>
          </div>
        </div>          
      </div>

      <div id="financial-segment" class="ui segment">
        <h4 class="ui dividing header">Financeiro</h4>
        <div class="ui form">
          <div class="fields">
            <div class="three wide field">
              <label class="form-label">% Impostos</label>
              <input class="money" :value="taxes" @blur="taxes = $event.target.value">
            </div>
            <div class="three wide field">
              <label class="form-label">% Cartão Crédito</label>
              <input class="money" :value="creditCardFee" @blur="creditCardFee = $event.target.value">
            </div>
            <div class="three wide field">
              <label class="form-label">% Cartão Débito</label>
              <input class="money" :value="debitCardFee" @blur="debitCardFee = $event.target.value">
            </div>
            <div class="three wide field">
              <label class="form-label">Natureza Laudos</label>
              <select-dropdown ref="certsNatureDropdown" @onChange="onChangeCertsNature" />
            </div>
            <div class="four wide field">
              <label class="form-label">Natureza Funcionários</label>
              <select-dropdown ref="employeesNatureDropdown" @onChange="onChangeEmployeesNature" />
            </div>
          </div>
        </div>
      </div>

      <button class="ui blue button m-t-10" :class="{ loading: updating }" @click="onConfirm()">
        <i class="check icon"></i> Atualizar
      </button>

    </div>

  </div>

</template>

<script>

  import { TheMask } from "vue-the-mask"
  import { BookmarkButton, SelectDropdown, SelectState } from "@/components"
  import Input from "@/utils/input"
  import onlyAdmin from "@/mixins/only-admin"  
  import CompanyService from "@/services/company-service"
  import FinancialNatureService from "@/services/financial-nature-service"

  export default {

    name: "SettingsPage",

    mixins: [onlyAdmin],

    data() {
      return {
        loading: true,
        updating: false,
        title: "Configurações",
        company: {},
        service: new CompanyService(),
      }
    },    

    components: {
      BookmarkButton,
      TheMask,
      SelectDropdown,
      SelectState,
    },

    mounted() {
      
      Input.maskFloat()

      this.loadCompany()
    },

    computed: {

      taxes: {
        get() {
          return Input.floatToString(this.company.taxes)
        },
        set(value) {
          this.company.taxes = Input.stringToFloat(value)    
        },
      },

      creditCardFee: {
        get() {
          return Input.floatToString(this.company.creditCardFee)
        },
        set(value) {
          this.company.creditCardFee = Input.stringToFloat(value)    
        },
      },

      debitCardFee: {
        get() {
          return Input.floatToString(this.company.debitCardFee)
        },
        set(value) {
          this.company.debitCardFee = Input.stringToFloat(value)    
        },
      },
    },

    methods: {

      async loadCompany() {

        this.loading = true

        try {

          this.company = await this.service.getCompany()

          await this.loadNatures()

          if (this.company.state)
            this.$refs.selectState.setSelected(this.company.state)
          
        } catch (error) {

          this.$errorHandler(error)
        
        } finally {
        
          this.loading = false
        }
      },

      async loadNatures() {

        try {

          const natures = await new FinancialNatureService().findAll({
            attributes: ["id", "type", "description"],
            order: "description",
          })

          const incomeNatures = natures.filter((nature) => nature.type == "income")
          const expenseNatures = natures.filter((nature) => nature.type == "expense")

          this.$refs.certsNatureDropdown.setValues(incomeNatures, this.company.certsNatureId)
          this.$refs.employeesNatureDropdown.setValues(expenseNatures, this.company.employeesNatureId)
          
        } catch (error) {
          
          this.$errorHandler(error)
        }
      },

      onChangeState(state) {
        this.company.state = state
      },

      onChangeCertsNature(id) {
        this.company.certsNatureId = id
      },

      onChangeEmployeesNature(id) {
        this.company.employeesNatureId = id
      },

      async onConfirm() {

        if (!this.validData())
          return

        this.updating = true
        
        try {

          await this.service.update(this.company)

          this.$msg.success("Dados atualizados com sucesso")
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.updating = false
        }
      },

      validData() {

        if (this.updating)
          return false
        
        let isOk = true
        let message = ""
        const { name, shortName, cnpj } = this.company

        if (!name) {
          message = "Informe a Razão Social"
        } else if (!shortName) {
          message = "Informe o Nome Fantasia"
        } else if (!cnpj) {
          message = "Informe o CNPJ"
        }

        if (message) {
          isOk = false
          this.$msg.info(message)
        }

        return isOk
      },
    },
  }

</script>
